import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { ReloadOutlined, CheckOutlined } from "@ant-design/icons";
import CustomButton from "../../components/CustomButton";

export interface SignaturePadRef {
  clear: () => void;
  save: () => void;
  hasContent: () => boolean;
}

interface SignaturePadProps {
  width?: number;
  height?: number;
  onSave?: (dataUrl: string) => void;
  onClear?: () => void;
  onContentChange?: (hasContent: boolean) => void;
  showControls?: boolean;
  defaultValue?: string;
  backgroundName?: string; // 背景显示的姓名
}

export const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  ({ width = 500, height = 200, onSave, onClear, onContentChange, showControls = true, defaultValue, backgroundName }, ref) => {
    // 双层 Canvas：背景层显示姓名，签名层捕获用户签名
    const backgroundCanvasRef = useRef<HTMLCanvasElement>(null);
    const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasContent, setHasContent] = useState(false);
    const [showExistingSignature, setShowExistingSignature] = useState(false);

    // 绘制背景姓名的函数（使用 useCallback 避免依赖问题）
    const drawBackgroundName = useCallback(() => {
      const canvas = backgroundCanvasRef.current;
      if (!canvas || !backgroundName) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // 清空背景画布
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 保存当前绘图状态
      ctx.save();

      // 设置字体样式 - 使用正楷字体
      // 优先使用系统正楷字体，回退到其他中文字体
      // iPad/iOS 上 "Kaiti SC" 可用，Windows 上 "KaiTi"/"楷体" 可用
      const fontSize = Math.min(canvas.width, canvas.height) * 0.25;
      ctx.font = `${fontSize}px "Kaiti SC", "STKaiti", "KaiTi", "楷体", serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // 设置颜色和透明度 - 浅灰色，15% 透明度，不干扰签名
      ctx.fillStyle = "rgba(200, 200, 200, 0.15)";

      // 在画布中心绘制文字
      ctx.fillText(backgroundName, canvas.width / 2, canvas.height / 2);

      // 恢复绘图状态
      ctx.restore();
    }, [backgroundName]);

    // 初始化背景层
    useEffect(() => {
      drawBackgroundName();
    }, [drawBackgroundName]);

    // 初始化签名层
    useEffect(() => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // 设置签名画笔样式，让签名更显眼
      ctx.lineWidth = 6;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000000";

      // 仅重置签名层；已有签名通过独立图片层回显，避免跨域图片污染 canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setShowExistingSignature(!!defaultValue);
      setHasContent(!!defaultValue);
    }, [defaultValue]);

    useImperativeHandle(ref, () => ({
      clear: () => clear(),
      save: () => save(),
      hasContent: () => hasContent,
    }));

    useEffect(() => {
      onContentChange?.(hasContent);
    }, [hasContent, onContentChange]);

    const preventNativeSelection = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      if (showExistingSignature) {
        return;
      }

      setIsDrawing(true);

      const rect = canvas.getBoundingClientRect();
      const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
      const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return;
      e.preventDefault();

      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
      const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        setHasContent(true);
      }
    };

    const clear = () => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // 清除签名层
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setShowExistingSignature(false);
      setHasContent(false);

      // 重绘背景层（背景层不需要清除，因为它是独立的）
      drawBackgroundName();

      onClear?.();
    };

    const save = () => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      if (showExistingSignature && defaultValue) {
        onSave?.(defaultValue);
        return;
      }

      // 只导出签名层的内容（不包含背景姓名）
      const dataUrl = canvas.toDataURL("image/png");
      onSave?.(dataUrl);
    };

    return (
      <div
        className="signature-pad-container"
        onContextMenu={preventNativeSelection}
        onDragStart={preventNativeSelection}
        style={{
          position: "relative",
          display: "inline-block",
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
          WebkitUserDrag: "none",
          touchAction: "none",
        }}
      >
        {/* 背景层 Canvas - 显示姓名水印 */}
        <canvas
          ref={backgroundCanvasRef}
          width={width}
          height={height}
          onContextMenu={preventNativeSelection}
          onDragStart={preventNativeSelection}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            touchAction: "none",
            pointerEvents: "none", // 背景层不响应用户交互
            WebkitUserSelect: "none",
            userSelect: "none",
            WebkitTouchCallout: "none",
            WebkitTapHighlightColor: "transparent",
            WebkitUserDrag: "none",
          }}
        />

        {showExistingSignature && defaultValue && (
          <img
            src={defaultValue}
            alt="existing-signature"
            draggable={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width,
              height,
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              pointerEvents: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
              WebkitTapHighlightColor: "transparent",
              objectFit: "fill",
            }}
          />
        )}

        {/* 签名层 Canvas - 捕获用户签名 */}
        <canvas
          ref={signatureCanvasRef}
          width={width}
          height={height}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onContextMenu={preventNativeSelection}
          onDragStart={preventNativeSelection}
          style={{
            position: "relative",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            cursor: "crosshair",
            touchAction: "none",
            backgroundColor: "transparent", // 签名层透明，显示下方背景层
            WebkitUserSelect: "none",
            userSelect: "none",
            WebkitTouchCallout: "none",
            WebkitTapHighlightColor: "transparent",
            WebkitUserDrag: "none",
          }}
        />

        {showControls && (
          <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
            <CustomButton icon={<ReloadOutlined />} onClick={clear}>
              清除
            </CustomButton>
            <CustomButton type="primary" icon={<CheckOutlined />} onClick={save} disabled={!hasContent}>
              确认
            </CustomButton>
          </div>
        )}
      </div>
    );
  }
);

SignaturePad.displayName = "SignaturePad";
